import { useState, useCallback, useEffect } from 'react';
import { encrypt, decrypt } from 'utils/cryptoUtils';

interface CachedData<T> {
  data: T;
  timestamp: number;
}

interface UseLocalStorageOptions {
  key: string;
  expirationMs?: number;
  encrypt?: boolean;
}

interface UseLocalStorageReturn<T> {
  data: T | null;
  setData: (value: T) => Promise<void>;
  removeData: () => void;
  isExpired: () => boolean;
  getCachedData: () => T | null;
  isInitialized: boolean;
}

/**
 * Custom hook for localStorage with optional encryption and expiration
 * @param options - Configuration options
 * @returns Object with data, setData, removeData, isExpired, and getCachedData functions
 */
export function useLocalStorage<T>(
  options: UseLocalStorageOptions,
): UseLocalStorageReturn<T> {
  const { key, expirationMs, encrypt: shouldEncrypt = true } = options;
  const [data, setDataState] = useState<T | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get cached data from localStorage
  const getCachedData = useCallback((): T | null => {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      let parsed: CachedData<T>;

      if (shouldEncrypt) {
        // For encrypted data, we need to decrypt first
        // Since decrypt is async, we'll handle it differently
        // For now, return null and handle async decryption in useEffect
        return null;
      } else {
        parsed = JSON.parse(stored) as CachedData<T>;
      }

      // Check expiration
      if (expirationMs) {
        const now = Date.now();
        const age = now - parsed.timestamp;
        if (age >= expirationMs) {
          // Cache expired
          localStorage.removeItem(key);
          return null;
        }
      }

      return parsed.data;
    } catch (error) {
      console.error(`Error reading cache for key "${key}":`, error);
      localStorage.removeItem(key);
      return null;
    }
  }, [key, expirationMs, shouldEncrypt]);

  // Load data from cache on mount or when key changes
  useEffect(() => {
    // Reset state when key changes
    setIsInitialized(false);
    setDataState(null);

    const loadCachedData = async () => {
      if (shouldEncrypt) {
        try {
          const encrypted = localStorage.getItem(key);
          if (encrypted) {
            const decrypted = await decrypt(encrypted);
            const parsed = JSON.parse(decrypted) as CachedData<T>;

            // Check expiration
            if (expirationMs) {
              const now = Date.now();
              const age = now - parsed.timestamp;
              if (age >= expirationMs) {
                localStorage.removeItem(key);
                setDataState(null);
                setIsInitialized(true);
                return;
              }
            }

            setDataState(parsed.data);
          }
          setIsInitialized(true);
        } catch (error) {
          console.error(
            `Error reading encrypted cache for key "${key}":`,
            error,
          );
          localStorage.removeItem(key);
          setDataState(null);
          setIsInitialized(true);
        }
      } else {
        const cached = getCachedData();
        setDataState(cached);
        setIsInitialized(true);
      }
    };

    void loadCachedData();
  }, [key, expirationMs, shouldEncrypt, getCachedData]);

  // Set data to localStorage
  const setData = useCallback(
    async (value: T): Promise<void> => {
      try {
        const cacheData: CachedData<T> = {
          data: value,
          timestamp: Date.now(),
        };

        if (shouldEncrypt) {
          const jsonData = JSON.stringify(cacheData);
          const encrypted = await encrypt(jsonData);
          localStorage.setItem(key, encrypted);
        } else {
          localStorage.setItem(key, JSON.stringify(cacheData));
        }

        setDataState(value);
      } catch (error) {
        console.error(`Error saving to cache for key "${key}":`, error);
      }
    },
    [key, shouldEncrypt],
  );

  // Remove data from localStorage
  const removeData = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setDataState(null);
    } catch (error) {
      console.error(`Error removing cache for key "${key}":`, error);
    }
  }, [key]);

  // Check if cached data is expired
  const isExpired = useCallback((): boolean => {
    if (!expirationMs) return false;

    try {
      const stored = localStorage.getItem(key);
      if (!stored) return true;

      let parsed: CachedData<T>;

      if (shouldEncrypt) {
        // For encrypted data, we can't check expiration synchronously
        // Return false to be safe (async check will handle it)
        return false;
      } else {
        parsed = JSON.parse(stored) as CachedData<T>;
      }

      const now = Date.now();
      const age = now - parsed.timestamp;
      return age >= expirationMs;
    } catch {
      return true;
    }
  }, [key, expirationMs, shouldEncrypt]);

  return {
    data,
    setData,
    removeData,
    isExpired,
    getCachedData,
    isInitialized,
  };
}
